// The browser's own QR decoder (Shape Detection API: Chrome and Edge on Android, ChromeOS, macOS). Not in lib.dom yet.
// Kept apart from the scanner's view so a page can ask whether it can scan without loading the camera code.
interface DetectedBarcode { rawValue: string }
export interface BarcodeDetectorLike { detect(source: CanvasImageSource): Promise<DetectedBarcode[]> }
interface BarcodeDetectorCtor {
  new (opts: { formats: string[] }): BarcodeDetectorLike;
  getSupportedFormats(): Promise<string[]>;
}
export const detectorCtor = () => (globalThis as { BarcodeDetector?: BarcodeDetectorCtor }).BarcodeDetector;

let native: Promise<boolean> | null = null;
/** Whether the browser decodes QR codes itself (BarcodeDetector with `qr_code`: Chrome and Edge on Android, ChromeOS, macOS). */
export function nativeQrSupported(): Promise<boolean> {
  native ??= (async () => {
    const D = detectorCtor();
    if (!D) return false;
    try {
      return (await D.getSupportedFormats()).includes('qr_code');
    } catch {
      return false;
    }
  })();
  return native;
}

/**
 * Whether the camera can scan here at all: a secure context and a camera API. Without the browser's own decoder (iOS
 * Safari, Firefox) a small decoder (jsQR) loads on first use; the typed serial is always beside it.
 */
export async function qrScanSupported(): Promise<boolean> {
  return window.isSecureContext && !!navigator.mediaDevices?.getUserMedia;
}

/** Decodes the QR codes of one video frame: the browser's detector, or jsQR on a downscaled canvas copy. */
export type FrameDecoder = (video: HTMLVideoElement) => Promise<string[]>;
const FALLBACK_EDGE = 640;
export async function frameDecoder(): Promise<FrameDecoder | null> {
  const D = detectorCtor();
  if (D && (await nativeQrSupported())) {
    const d = new D({ formats: ['qr_code'] });
    return async (v) => (await d.detect(v)).map((c) => c.rawValue).filter(Boolean);
  }
  try {
    // loaded only here, never with the page (its own chunk)
    const { default: jsQR } = await import('jsqr');
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return null;
    return async (v) => {
      const scale = Math.min(1, FALLBACK_EDGE / Math.max(v.videoWidth, v.videoHeight, 1));
      const w = Math.max(1, Math.round(v.videoWidth * scale));
      const h = Math.max(1, Math.round(v.videoHeight * scale));
      if (canvas.width !== w) canvas.width = w;
      if (canvas.height !== h) canvas.height = h;
      ctx.drawImage(v, 0, 0, w, h);
      const r = jsQR(ctx.getImageData(0, 0, w, h).data, w, h, { inversionAttempts: 'dontInvert' });
      return r?.data ? [r.data] : [];
    };
  } catch {
    return null;
  }
}
